import { z } from 'zod';

/**
 * SignCare's signature-placement contract.
 *
 * READ THIS BEFORE TOUCHING THE MATH. The field names lie:
 *
 *   X1 = left   (points from the page's LEFT edge)
 *   X2 = WIDTH  (points)  ← not a second x coordinate
 *   Y1 = top    (points from the page's TOP edge)
 *   Y2 = HEIGHT (points)  ← not a second y coordinate
 *
 * So the origin is TOP-left, like CSS — not bottom-left, like PDF. The portal
 * writes these straight out of the element's `style.left/width/top/height`
 * (template-upload.component.ts `saveTemplate`), and the server flips the axis
 * itself using PageSize. Emitting true x2/y2 pairs, or PDF-origin values, produces
 * boxes that render mirrored or off-page — and nothing warns you.
 *
 * `PageSize` is the page HEIGHT. Also not a typo.
 */
export const pdfCoordinateSchema = z.object({
  X1: z.number(),
  X2: z.number(),
  Y1: z.number(),
  Y2: z.number(),
});

export const pdfPageSchema = z.object({
  PageNumber: z.number().int(),
  /** Page height in points. */
  PageSize: z.number(),
  /** Page width in points. */
  PageWidth: z.number(),
  PDFCoordinates: z.array(pdfCoordinateSchema),
});

export type PdfCoordinate = z.infer<typeof pdfCoordinateSchema>;
export type PdfPage = z.infer<typeof pdfPageSchema>;

/** How a box is held while the author is moving it: plain top-left geometry. */
export interface SignatureBox {
  id: string;
  signerId: string;
  /** 1-based, matching PageNumber on the wire. */
  pageNumber: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A page's size in points — what every conversion is relative to. */
export interface PageSize {
  widthPt: number;
  heightPt: number;
}

/**
 * The default signature box, in points.
 *
 * Matches the portal's `DefaultSignBox` so a template authored in Studio and one
 * authored in the portal produce the same-sized signature on the final PDF.
 */
export const DEFAULT_SIGN_BOX = { width: 140, height: 50 } as const;

/**
 * Groups one signer's boxes into the per-page shape the API expects.
 *
 * Pages with no boxes are omitted — the portal does the same, and sending an empty
 * page array reads to the server as "this signer signs on that page".
 */
export function toPdfPages(
  boxes: readonly SignatureBox[],
  signerId: string,
  pageSizes: ReadonlyMap<number, PageSize>,
): PdfPage[] {
  const byPage = new Map<number, PdfCoordinate[]>();

  for (const box of boxes) {
    if (box.signerId !== signerId) continue;

    const list = byPage.get(box.pageNumber) ?? [];
    list.push({
      X1: round(box.left),
      X2: round(box.width),
      Y1: round(box.top),
      Y2: round(box.height),
    });
    byPage.set(box.pageNumber, list);
  }

  return [...byPage.entries()]
    .sort(([a], [b]) => a - b)
    .map(([pageNumber, coordinates]) => {
      const size = pageSizes.get(pageNumber);
      return {
        PageNumber: pageNumber,
        PageSize: round(size?.heightPt ?? 0),
        PageWidth: round(size?.widthPt ?? 0),
        PDFCoordinates: coordinates,
      };
    });
}

/** Rebuilds editable boxes from a saved template. */
export function fromPdfPages(pages: readonly PdfPage[], signerId: string): SignatureBox[] {
  return pages.flatMap((page) =>
    page.PDFCoordinates.map((coordinate) => ({
      id: crypto.randomUUID(),
      signerId,
      pageNumber: page.PageNumber,
      left: coordinate.X1,
      top: coordinate.Y1,
      width: coordinate.X2,
      height: coordinate.Y2,
    })),
  );
}

/**
 * Keeps a box inside its page.
 *
 * A box hanging off the edge is not a cosmetic problem: the server places the
 * signature at these coordinates on the real PDF, where there is no overflow to
 * scroll into — the signature is simply cut off or lost.
 */
export function clampToPage(box: SignatureBox, page: PageSize): SignatureBox {
  const width = Math.min(box.width, page.widthPt);
  const height = Math.min(box.height, page.heightPt);

  return {
    ...box,
    width,
    height,
    left: Math.max(0, Math.min(box.left, page.widthPt - width)),
    top: Math.max(0, Math.min(box.top, page.heightPt - height)),
  };
}

/** Two decimals is finer than a PDF point is worth arguing about. */
function round(value: number): number {
  return Math.round(value * 100) / 100;
}
